/* eslint-disable no-unused-vars */
// ScheduleFinalInterviewScreen_Fixed.js
// Fixed version with proper error handling and null checks
// Fully functional and productive final interview scheduling screen

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Alert,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  Modal,
  FlatList,
  ActivityIndicator,
  Switch,
  StatusBar,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  AntDesign,
  FontAwesome5,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

const ScheduleFinalInterviewScreen = ({ navigation, route }) => {
  // Safely extract route params with fallbacks
  const candidate = route?.params?.candidate || {
    id: 'unknown',
    name: 'Unknown Candidate',
    position: 'Unknown Position',
    email: 'unknown@example.com',
    phone: '+44 0000 000000',
    photo: null,
  };
  
  const onInterviewScheduled = route?.params?.onInterviewScheduled || (() => {});

  // State management
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    interviewers: [],
    interviewType: '',
    duration: 90,
    notes: '',
    notifyCandidate: true,
    notifyInterviewers: true,
    sendCalendarInvite: true,
    reminderTime: '24',
    priority: 'high',
    preparationTime: 15,
    bufferTime: 15,
    meetingLink: '',
    recordInterview: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showInterviewerPicker, setShowInterviewerPicker] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]);


  // ── Company logo loader (auto-injected) ─────────────────────────────────────
  // Hydrates from cache for instant paint, then refreshes from
  // /api/company-profile/:id so any logo / name change made elsewhere
  // (Settings, Dashboard, etc.) propagates to this screen on next open.
  const [_companyLogo,  _setCompanyLogo]  = useState(null);
  const [_companyName,  _setCompanyName]  = useState('');
  useEffect(() => { _loadCompanyLogo(); }, []);
  const _loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        _setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) _setCompanyName(profile.name || profile.company_name);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await fetch(`https://api.jobeng.ai/api/company-profile/${cid}`, {
            headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' }
          });
          if (res.ok) {
            const json = await res.json();
            const row = (json?.data && !Array.isArray(json.data)) ? json.data
                     : (Array.isArray(json?.data) ? json.data[0] : json);
            if (row && (row.logo || row.name || row.company_name)) {
              if (row.logo) _setCompanyLogo(row.logo);
              const nm = row.name || row.company_name;
              if (nm) _setCompanyName(nm);
              await AsyncStorage.setItem('company_profile', JSON.stringify(row));
              console.log('[CompanyLogo] refreshed from API for cid=' + cid);
            }
          }
        } catch (e) { /* offline — cache is enough */ }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };
  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Check availability when date or interviewers change
  useEffect(() => {
    if (formData.date && formData.interviewers.length > 0) {
      checkAvailability();
    }
  }, [formData.date, formData.interviewers]);

  // Load all necessary data
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load interviewers
      // Load interviewers - API first
      let interviewersLoaded = false;
      try {
        const resp = await apiRequest('final-interviewers');
        if (resp.success && resp.data) {
          setInterviewers(resp.data);
          await AsyncStorage.setItem('finalInterviewers', JSON.stringify(resp.data));
          interviewersLoaded = true;
        }
      } catch (apiErr) {
        const storedInterviewers = await AsyncStorage.getItem('finalInterviewers');
        if (storedInterviewers) {
          setInterviewers(JSON.parse(storedInterviewers));
          interviewersLoaded = true;
        }
      }
      if (!interviewersLoaded) {
        const defaultInterviewers = generateDefaultInterviewers();
        setInterviewers(defaultInterviewers);
        await AsyncStorage.setItem('finalInterviewers', JSON.stringify(defaultInterviewers));
        try { await apiRequest('final-interviewers', { method: 'POST', body: { interviewers: defaultInterviewers } }); } catch(e) {}
      }

      // Load locations
      // Load locations - API first
      let locationsLoaded = false;
      try {
        const resp = await apiRequest('final-interview-locations');
        if (resp.success && resp.data) {
          setLocations(resp.data);
          await AsyncStorage.setItem('finalInterviewLocations', JSON.stringify(resp.data));
          locationsLoaded = true;
        }
      } catch (apiErr) {
        const storedLocations = await AsyncStorage.getItem('finalInterviewLocations');
        if (storedLocations) {
          setLocations(JSON.parse(storedLocations));
          locationsLoaded = true;
        }
      }
      if (!locationsLoaded) {
        const defaultLocations = generateDefaultLocations();
        setLocations(defaultLocations);
        await AsyncStorage.setItem('finalInterviewLocations', JSON.stringify(defaultLocations));
        try { await apiRequest('final-interview-locations', { method: 'POST', body: { locations: defaultLocations } }); } catch(e) {}
      }

      // Load interview types
      // Load types - API first
      let typesLoaded = false;
      try {
        const resp = await apiRequest('final-interview-types');
        if (resp.success && resp.data) {
          setInterviewTypes(resp.data);
          await AsyncStorage.setItem('finalInterviewTypes', JSON.stringify(resp.data));
          typesLoaded = true;
        }
      } catch (apiErr) {
        const storedTypes = await AsyncStorage.getItem('finalInterviewTypes');
        if (storedTypes) {
          setInterviewTypes(JSON.parse(storedTypes));
          typesLoaded = true;
        }
      }
      if (!typesLoaded) {
        const defaultTypes = generateDefaultInterviewTypes();
        setInterviewTypes(defaultTypes);
        await AsyncStorage.setItem('finalInterviewTypes', JSON.stringify(defaultTypes));
        try { await apiRequest('final-interview-types', { method: 'POST', body: { types: defaultTypes } }); } catch(e) {}
      }

    } catch (error) {
      console.error('Error loading data:', error);
      Alert.alert('Error', 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate default interviewers for final interviews (senior staff)
  const generateDefaultInterviewers = () => [
    {
      id: '1',
      name: 'Dr. Sarah Mitchell',
      title: 'Chief Technology Officer',
      department: 'Executive',
      email: 'sarah.mitchell@company.com',
      phone: '+44 7700 900123',
      photo: null, // Safe default
      expertise: ['Technology Strategy', 'Leadership', 'Innovation', 'Scaling'],
      availability: generateExecutiveAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 2,
      rating: 5.0,
      totalInterviews: 67,
      seniorityLevel: 'C-Level',
    },
    {
      id: '2',
      name: 'John Harrison',
      title: 'Chief Executive Officer',
      department: 'Executive',
      email: 'john.harrison@company.com',
      phone: '+44 7700 900124',
      photo: null,
      expertise: ['Business Strategy', 'Leadership', 'Vision', 'Growth'],
      availability: generateExecutiveAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['In-person', 'Video'],
      maxInterviewsPerDay: 1,
      rating: 5.0,
      totalInterviews: 34,
      seniorityLevel: 'C-Level',
    },
    {
      id: '3',
      name: 'Lisa Chen',
      title: 'Chief Human Resources Officer',
      department: 'Executive',
      email: 'lisa.chen@company.com',
      phone: '+44 7700 900125',
      photo: null,
      expertise: ['People Strategy', 'Culture', 'Talent Management', 'Leadership'],
      availability: generateExecutiveAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 3,
      rating: 4.9,
      totalInterviews: 89,
      seniorityLevel: 'C-Level',
    },
    {
      id: '4',
      name: 'Robert Taylor',
      title: 'Vice President of Engineering',
      department: 'Engineering',
      email: 'robert.taylor@company.com',
      phone: '+44 7700 900126',
      photo: null,
      expertise: ['Engineering Leadership', 'Technical Strategy', 'Team Building'],
      availability: generateSeniorAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 2,
      rating: 4.8,
      totalInterviews: 123,
      seniorityLevel: 'VP',
    },
    {
      id: '5',
      name: 'Maria Rodriguez',
      title: 'Chief Financial Officer',
      department: 'Finance',
      email: 'maria.rodriguez@company.com',
      phone: '+44 7700 900127',
      photo: null,
      expertise: ['Financial Strategy', 'Business Planning', 'Risk Management'],
      availability: generateExecutiveAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 2,
      rating: 4.9,
      totalInterviews: 45,
      seniorityLevel: 'C-Level',
    },
    {
      id: '6',
      name: 'David Kim',
      title: 'Head of Product',
      department: 'Product',
      email: 'david.kim@company.com',
      phone: '+44 7700 900128',
      photo: null,
      expertise: ['Product Strategy', 'Innovation', 'Market Analysis', 'Leadership'],
      availability: generateSeniorAvailability(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 3,
      rating: 4.8,
      totalInterviews: 78,
      seniorityLevel: 'Director',
    },
  ];

  // Generate executive availability (very limited)
  const generateExecutiveAvailability = () => {
    const schedule = {};
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) {
        schedule[dateStr] = [];
        continue;
      }
      
      // Very limited availability for executives
      const slots = [];
      const possibleSlots = ['10:00', '11:00', '14:00', '15:00'];
      
      // Only 30% chance of being available
      possibleSlots.forEach(slot => {
        if (Math.random() > 0.7) {
          slots.push(slot);
        }
      });
      
      schedule[dateStr] = slots;
    }
    
    return schedule;
  };

  // Generate senior staff availability (limited)
  const generateSeniorAvailability = () => {
    const schedule = {};
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) {
        schedule[dateStr] = [];
        continue;
      }
      
      // Limited availability for senior staff
      const slots = [];
      const possibleSlots = [
        '09:00', '10:00', '11:00', '14:00', '15:00', '16:00'
      ];
      
      // 50% chance of being available
      possibleSlots.forEach(slot => {
        if (Math.random() > 0.5) {
          slots.push(slot);
        }
      });
      
      schedule[dateStr] = slots;
    }
    
    return schedule;
  };

  // Generate default locations for final interviews
  const generateDefaultLocations = () => [
    {
      id: '1',
      name: 'Executive Boardroom',
      type: 'Physical',
      capacity: 12,
      floor: '10th Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Large Screen', 'Video Conference', 'Premium Seating', 'Catering'],
      bookingRequired: true,
      available: true,
      prestige: 'high',
    },
    {
      id: '2',
      name: 'CEO Conference Room',
      type: 'Physical',
      capacity: 8,
      floor: '10th Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Video Conference', 'Whiteboard', 'Premium Audio'],
      bookingRequired: true,
      available: true,
      prestige: 'high',
    },
    {
      id: '3',
      name: 'Senior Leadership Suite',
      type: 'Physical',
      capacity: 6,
      floor: '9th Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Video Conference', 'Presentation Screen', 'Coffee Service'],
      bookingRequired: true,
      available: true,
      prestige: 'high',
    },
    {
      id: '4',
      name: 'Executive Zoom Room',
      type: 'Virtual',
      capacity: 50,
      platform: 'Zoom',
      features: ['HD Video', 'Screen Sharing', 'Recording', 'Breakout Rooms'],
      meetingLink: 'https://zoom.us/j/executive-room',
      dialIn: '+44 203 481 5237',
      available: true,
      prestige: 'medium',
    },
    {
      id: '5',
      name: 'Microsoft Teams Executive',
      type: 'Virtual',
      capacity: 100,
      platform: 'Teams',
      features: ['HD Video', 'Screen Sharing', 'Recording', 'Live Captions'],
      meetingLink: 'https://teams.microsoft.com/l/executive-meetup',
      available: true,
      prestige: 'medium',
    },
    {
      id: '6',
      name: 'Private Interview Room',
      type: 'Physical',
      capacity: 4,
      floor: '8th Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Video Conference', 'Quiet Environment', 'Premium Seating'],
      bookingRequired: true,
      available: true,
      prestige: 'medium',
    },
  ];

  // Generate default interview types for final interviews
  const generateDefaultInterviewTypes = () => [
    {
      id: '1',
      name: 'Executive Interview',
      duration: 90,
      color: '#0e4e96',
      description: 'Final interview with C-level executives',
      stages: ['Leadership Discussion', 'Strategic Thinking', 'Company Vision', 'Cultural Fit'],
      requiredInterviewers: ['C-Level'],
      preparationTime: 20,
      followUpRequired: true,
      importance: 'critical',
    },
    {
      id: '2',
      name: 'Panel Interview',
      duration: 120,
      color: '#8B5CF6',
      description: 'Multi-interviewer panel with senior leadership',
      stages: ['Introductions', 'Technical Discussion', 'Leadership Assessment', 'Q&A'],
      requiredInterviewers: ['Multiple Senior'],
      preparationTime: 25,
      followUpRequired: true,
      importance: 'critical',
    },
    {
      id: '3',
      name: 'Cultural Fit Assessment',
      duration: 60,
      color: '#10B981',
      description: 'Deep dive into cultural alignment and values',
      stages: ['Values Discussion', 'Scenario Analysis', 'Team Dynamics', 'Growth Mindset'],
      requiredInterviewers: ['HR Executive', 'Department Head'],
      preparationTime: 15,
      followUpRequired: true,
      importance: 'high',
    },
    {
      id: '4',
      name: 'Strategic Leadership Interview',
      duration: 90,
      color: '#F59E0B',
      description: 'Assessment of strategic thinking and leadership potential',
      stages: ['Strategic Planning', 'Decision Making', 'Change Management', 'Innovation'],
      requiredInterviewers: ['Senior Leadership'],
      preparationTime: 20,
      followUpRequired: true,
      importance: 'critical',
    },
    {
      id: '5',
      name: 'CEO Final Interview',
      duration: 60,
      color: '#EF4444',
      description: 'Final decision interview with CEO',
      stages: ['Vision Alignment', 'Leadership Philosophy', 'Growth Potential', 'Final Decision'],
      requiredInterviewers: ['CEO'],
      preparationTime: 15,
      followUpRequired: true,
      importance: 'critical',
    },
  ];

  // Check availability for selected date and interviewers
  const checkAvailability = () => {
    const selectedDate = formData.date;
    const selectedInterviewers = formData.interviewers;

    if (!selectedDate || selectedInterviewers.length === 0) {
      setAvailableTimeSlots([]);
      setConflicts([]);
      return;
    }

    // Find common available time slots
    let commonSlots = [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
    ];

    selectedInterviewers.forEach(interviewerId => {
      const interviewer = interviewers.find(i => i.id === interviewerId);
      if (interviewer?.availability?.[selectedDate]) {
        commonSlots = commonSlots.filter(slot =>
          interviewer.availability[selectedDate].includes(slot)
        );
      } else {
        commonSlots = [];
      }
    });

    setAvailableTimeSlots(commonSlots);

    // Check for conflicts
    const newConflicts = [];
    if (commonSlots.length === 0 && selectedInterviewers.length > 0) {
      newConflicts.push({
        type: 'availability',
        message: 'No common available time slots for selected interviewers on this date',
        severity: 'high',
      });
    }

    // Check for weekend scheduling
    const selectedDateObj = new Date(selectedDate);
    if (selectedDateObj.getDay() === 0 || selectedDateObj.getDay() === 6) {
      newConflicts.push({
        type: 'weekend',
        message: 'Final interview scheduled on weekend - consider rescheduling',
        severity: 'medium',
      });
    }

    // Check for short notice (less than 48 hours for final interviews)
    const now = new Date();
    const timeDiff = selectedDateObj.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    if (hoursDiff < 48 && hoursDiff > 0) {
      newConflicts.push({
        type: 'short_notice',
        message: 'Final interview scheduled with less than 48 hours notice',
        severity: 'medium',
      });
    }

    setConflicts(newConflicts);
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    // Date validation
    if (!formData.date) {
      newErrors.date = 'Interview date is required';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.date = 'Interview date cannot be in the past';
      }
    }

    // Time validation
    if (!formData.time) {
      newErrors.time = 'Interview time is required';
    } else if (!availableTimeSlots.includes(formData.time)) {
      newErrors.time = 'Selected time is not available for all interviewers';
    }

    // Location validation
    if (!formData.location) {
      newErrors.location = 'Interview location is required';
    }

    // Interviewers validation
    if (formData.interviewers.length === 0) {
      newErrors.interviewers = 'At least one interviewer must be selected';
    }

    // Interview type validation
    if (!formData.interviewType) {
      newErrors.interviewType = 'Interview type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null,
      }));
    }

    // Auto-set duration based on interview type
    if (field === 'interviewType') {
      const selectedType = interviewTypes.find(type => type.id === value);
      if (selectedType) {
        setFormData(prev => ({
          ...prev,
          duration: selectedType.duration,
          preparationTime: selectedType.preparationTime,
        }));
      }
    }
  };

  // Handle interviewer selection
  const handleInterviewerToggle = (interviewerId) => {
    const currentInterviewers = formData.interviewers;
    const newInterviewers = currentInterviewers.includes(interviewerId)
      ? currentInterviewers.filter(id => id !== interviewerId)
      : [...currentInterviewers, interviewerId];

    handleInputChange('interviewers', newInterviewers);
  };

  // Handle save with validation and conflict checking
  const handleSchedule = async () => {
    if (!validateForm()) {
      Alert.alert(
        'Validation Error',
        'Please correct the errors before scheduling.',
        [{ text: 'OK' }]
      );
      return;
    }

    // Check for high severity conflicts
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high');
    if (highSeverityConflicts.length > 0) {
      setShowConflictModal(true);
      return;
    }

    // Check for medium severity conflicts
    const mediumSeverityConflicts = conflicts.filter(c => c.severity === 'medium');
    if (mediumSeverityConflicts.length > 0) {
      Alert.alert(
        'Scheduling Conflicts',
        mediumSeverityConflicts.map(c => c.message).join('\n\n'),
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Proceed Anyway', onPress: () => performScheduling() },
        ]
      );
      return;
    }

    performScheduling();
  };

  // Perform the actual scheduling
  const performScheduling = async () => {
    setIsSaving(true);

    try {
      // Simulate API call with realistic delay
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Get interviewer names from IDs (with safe access)
      const interviewerNames = formData.interviewers.map(id => {
        const interviewer = interviewers.find(i => i.id === id);
        return interviewer ? interviewer.name : 'Unknown Interviewer';
      });

      // Get location details (with safe access)
      const locationDetails = locations.find(l => l.id === formData.location);
      const locationName = locationDetails ? locationDetails.name : 'Unknown Location';

      // Get interview type details (with safe access)
      const typeDetails = interviewTypes.find(t => t.id === formData.interviewType);
      const typeName = typeDetails ? typeDetails.name : 'Unknown Type';

      // Create scheduled interview object
      const scheduledInterview = {
        id: `final_interview_${Date.now()}`,
        candidateId: candidate.id,
        candidateName: candidate.name,
        candidatePosition: candidate.position || 'Unknown Position',
        candidateEmail: candidate.email,
        candidatePhone: candidate.phone,
        candidatePhoto: candidate.photo || null, // Safe handling of photo
        interviewDate: `${formData.date}T${formData.time}:00.000Z`,
        interviewLocation: locationName,
        interviewers: interviewerNames,
        interviewType: typeName,
        duration: formData.duration,
        status: 'Scheduled',
        priority: formData.priority,
        notes: formData.notes,
        preparationTime: formData.preparationTime,
        bufferTime: formData.bufferTime,
        meetingLink: formData.meetingLink,
        recordInterview: formData.recordInterview,
        notificationSettings: {
          candidateNotified: formData.notifyCandidate,
          interviewersNotified: formData.notifyInterviewers,
          calendarInviteSent: formData.sendCalendarInvite,
          reminderTime: formData.reminderTime,
        },
        createdAt: new Date().toISOString(),
        createdBy: 'HR Manager', // In real app, get from auth
        updatedAt: new Date().toISOString(),
        version: 1,
        interviewStage: 'Final',
        importance: typeDetails?.importance || 'high',
      };

      // Save to AsyncStorage
      try {
        const storedInterviews = await AsyncStorage.getItem('finalInterviews');
        let interviews = storedInterviews ? JSON.parse(storedInterviews) : [];
        interviews.push(scheduledInterview);
        try { await apiRequest('final-interviews', { method: 'POST', body: { interview: scheduledInterview } }); } catch(e) { console.warn('API save failed:', e.message); }
        await AsyncStorage.setItem('finalInterviews', JSON.stringify(interviews));
      } catch (storageError) {
        console.error('Error saving to storage:', storageError);
      }

      // Call the callback function
      onInterviewScheduled(scheduledInterview);

      // Show success message
      const scheduledDate = new Date(scheduledInterview.interviewDate);
      const dateStr = scheduledDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeStr = scheduledDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });

      const successMessage = `Final interview has been successfully scheduled for ${candidate.name} on ${dateStr} at ${timeStr}.${
        formData.notifyCandidate 
          ? '\n\nThe candidate has been notified via email.' 
          : '\n\nRemember to notify the candidate manually.'
      }${
        formData.sendCalendarInvite 
          ? '\nCalendar invites have been sent to all participants.' 
          : ''
      }`;

      Alert.alert(
        'Interview Scheduled',
        successMessage,
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]
      );

    } catch (error) {
      console.error('Error scheduling interview:', error);
      Alert.alert(
        'Error',
        'Failed to schedule interview. Please check your connection and try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    // Check if form has been modified
    const hasChanges = formData.date || formData.time || formData.interviewers.length > 0;

    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'You have unsaved changes. Are you sure you want to discard them?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => navigation.goBack(),
          },
        ]
      );
    } else {
      navigation.goBack();
    }
  };

  // Generate next available business days
  const generateNextBusinessDays = (count = 15) => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);

    while (dates.length < count) {
      // Skip weekends for final interviews
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Safe helper functions
  const safeGetInterviewerName = (id) => {
    const interviewer = interviewers.find(i => i.id === id);
    return interviewer ? interviewer.name : 'Unknown';
  };

  const safeGetLocationName = () => {
    const location = locations.find(l => l.id === formData.location);
    return location ? location.name : 'Select location';
  };

  const safeGetInterviewTypeName = () => {
    const type = interviewTypes.find(t => t.id === formData.interviewType);
    return type ? type.name : 'Select interview type';
  };

  const safeGetSelectedInterviewerNames = () => {
    const selectedInterviewers = interviewers.filter(i => formData.interviewers.includes(i.id));
    if (selectedInterviewers.length === 0) return 'Select interviewers';
    if (selectedInterviewers.length === 1) return selectedInterviewers[0].name;
    if (selectedInterviewers.length === 2) {
      return `${selectedInterviewers[0].name} and ${selectedInterviewers[1].name}`;
    }
    return `${selectedInterviewers[0].name} and ${selectedInterviewers.length - 1} others`;
  };

  // Render date picker modal
  const renderDatePicker = () => {
    const dates = generateNextBusinessDays();

    return (
      <Modal visible={showDatePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interview Date</Text>
              <TouchableOpacity onPress={() => setShowDatePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={dates}
              keyExtractor={(item, index) => index.toString()}
              style={styles.modalContent}
              renderItem={({ item: date }) => {
                const dateFormatted = date.toISOString().split('T')[0];
                const isToday = dateFormatted === new Date().toISOString().split('T')[0];
                const dateDisplay = date.toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });

                return (
                  <TouchableOpacity
                    style={[
                      styles.modalOption,
                      formData.date === dateFormatted && styles.selectedOption,
                    ]}
                    onPress={() => {
                      handleInputChange('date', dateFormatted);
                      setShowDatePicker(false);
                    }}
                  >
                    <View style={styles.dateOptionContent}>
                      <Text style={[
                        styles.modalOptionText,
                        formData.date === dateFormatted && styles.selectedOptionText,
                      ]}>
                        {dateDisplay}
                      </Text>
                      {isToday && (
                        <View style={styles.todayBadge}>
                          <Text style={styles.todayBadgeText}>Today</Text>
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render time picker modal
  const renderTimePicker = () => {
    return (
      <Modal visible={showTimePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interview Time</Text>
              <TouchableOpacity onPress={() => setShowTimePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              {availableTimeSlots.length > 0 ? (
                <FlatList
                  data={availableTimeSlots}
                  keyExtractor={(item) => item}
                  numColumns={3}
                  renderItem={({ item: time }) => (
                    <TouchableOpacity
                      style={[
                        styles.timeSlot,
                        formData.time === time && styles.selectedTimeSlot,
                      ]}
                      onPress={() => {
                        handleInputChange('time', time);
                        setShowTimePicker(false);
                      }}
                    >
                      <Text style={[
                        styles.timeSlotText,
                        formData.time === time && styles.selectedTimeSlotText,
                      ]}>
                        {time}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              ) : (
                <View style={styles.noAvailabilityContainer}>
                  <MaterialCommunityIcons name="calendar-remove" size={48} color="#CBD5E1" />
                  <Text style={styles.noAvailabilityTitle}>No Available Times</Text>
                  <Text style={styles.noAvailabilityText}>
                    No common time slots available for selected date and interviewers.
                    Please select a different date or different interviewers.
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // Render location picker modal
  const renderLocationPicker = () => {
    return (
      <Modal visible={showLocationPicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interview Location</Text>
              <TouchableOpacity onPress={() => setShowLocationPicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={locations}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: location }) => (
                <TouchableOpacity
                  style={[
                    styles.locationOption,
                    formData.location === location.id && styles.selectedLocationOption,
                  ]}
                  onPress={() => {
                    handleInputChange('location', location.id);
                    if (location.type === 'Virtual' && location.meetingLink) {
                      handleInputChange('meetingLink', location.meetingLink);
                    }
                    setShowLocationPicker(false);
                  }}
                >
                  <View style={styles.locationHeader}>
                    <View style={styles.locationInfo}>
                      <Text style={[
                        styles.locationName,
                        formData.location === location.id && styles.selectedLocationName,
                      ]}>
                        {location.name}
                      </Text>
                      <View style={styles.locationMeta}>
                        <View style={[
                          styles.locationTypeBadge,
                          { backgroundColor: getLocationTypeColor(location.type) }
                        ]}>
                          <Text style={styles.locationTypeBadgeText}>{location.type}</Text>
                        </View>
                        <Text style={styles.locationCapacity}>
                          Capacity: {location.capacity}
                        </Text>
                        {location.prestige && (
                          <View style={[
                            styles.prestigeBadge,
                            { backgroundColor: getPrestigeColor(location.prestige) }
                          ]}>
                            <Text style={styles.prestigeBadgeText}>
                              {String(location.prestige ?? '').toUpperCase()}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                    {formData.location === location.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                    )}
                  </View>
                  
                  {location.type === 'Physical' && (
                    <View style={styles.locationDetails}>
                      <Text style={styles.locationAddress}>
                        {location.floor && `${location.floor}, `}
                        {location.building && `${location.building}`}
                      </Text>
                      {location.amenities && (
                        <View style={styles.amenitiesContainer}>
                          {location.amenities.slice(0, 3).map((amenity, index) => (
                            <View key={index} style={styles.amenityChip}>
                              <Text style={styles.amenityText}>{amenity}</Text>
                            </View>
                          ))}
                          {location.amenities.length > 3 && (
                            <Text style={styles.moreAmenities}>
                              +{location.amenities.length - 3} more
                            </Text>
                          )}
                        </View>
                      )}
                    </View>
                  )}
                  
                  {location.type === 'Virtual' && (
                    <View style={styles.locationDetails}>
                      <Text style={styles.platformText}>Platform: {location.platform}</Text>
                      {location.features && (
                        <View style={styles.featuresContainer}>
                          {location.features.slice(0, 3).map((feature, index) => (
                            <View key={index} style={styles.featureChip}>
                              <Text style={styles.featureText}>{feature}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render interviewer picker modal
  const renderInterviewerPicker = () => {
    return (
      <Modal visible={showInterviewerPicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interviewers</Text>
              <TouchableOpacity onPress={() => setShowInterviewerPicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={interviewers}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: interviewer }) => {
                const isSelected = formData.interviewers.includes(interviewer.id);
                const hasAvailability = formData.date && 
                  interviewer.availability?.[formData.date]?.length > 0;

                return (
                  <TouchableOpacity
                    style={[
                      styles.interviewerOption,
                      isSelected && styles.selectedInterviewerOption,
                      !hasAvailability && formData.date && styles.unavailableInterviewerOption,
                    ]}
                    onPress={() => handleInterviewerToggle(interviewer.id)}
                  >
                    <View style={styles.interviewerHeader}>
                      <View style={styles.interviewerAvatar}>
                        <Text style={styles.interviewerInitials}>
                          {interviewer.name.split(' ').map(n => n[0]).join('')}
                        </Text>
                      </View>
                      
                      <View style={styles.interviewerInfo}>
                        <Text style={[
                          styles.interviewerName,
                          isSelected && styles.selectedInterviewerName,
                        ]}>
                          {interviewer.name}
                        </Text>
                        <Text style={styles.interviewerTitle}>{interviewer.title}</Text>
                        <Text style={styles.interviewerDepartment}>{interviewer.department}</Text>
                        
                        <View style={styles.interviewerMeta}>
                          <View style={styles.ratingContainer}>
                            <MaterialCommunityIcons name="star" size={14} color="#F59E0B" />
                            <Text style={styles.ratingText}>{interviewer.rating}</Text>
                          </View>
                          <View style={[
                            styles.seniorityBadge,
                            { backgroundColor: getSeniorityColor(interviewer.seniorityLevel) }
                          ]}>
                            <Text style={styles.seniorityBadgeText}>{interviewer.seniorityLevel}</Text>
                          </View>
                        </View>
                      </View>
                      
                      <View style={styles.selectionIndicator}>
                        {isSelected ? (
                          <MaterialCommunityIcons name="checkbox-marked-circle" size={24} color="#10B981" />
                        ) : (
                          <MaterialCommunityIcons name="checkbox-blank-circle-outline" size={24} color="#CBD5E1" />
                        )}
                      </View>
                    </View>
                    
                    {formData.date && (
                      <View style={styles.availabilityIndicator}>
                        {hasAvailability ? (
                          <View style={styles.availableIndicator}>
                            <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                            <Text style={styles.availableText}>
                              Available ({interviewer.availability[formData.date].length} slots)
                            </Text>
                          </View>
                        ) : (
                          <View style={styles.unavailableIndicator}>
                            <MaterialCommunityIcons name="close-circle" size={16} color="#EF4444" />
                            <Text style={styles.unavailableText}>Not available</Text>
                          </View>
                        )}
                      </View>
                    )}
                    
                    {interviewer.expertise && (
                      <View style={styles.expertiseContainer}>
                        {interviewer.expertise.slice(0, 3).map((skill, index) => (
                          <View key={index} style={styles.expertiseChip}>
                            <Text style={styles.expertiseText}>{skill}</Text>
                          </View>
                        ))}
                        {interviewer.expertise.length > 3 && (
                          <Text style={styles.moreExpertise}>
                            +{interviewer.expertise.length - 3} more
                          </Text>
                        )}
                      </View>
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render interview type picker modal
  const renderTypePicker = () => {
    return (
      <Modal visible={showTypePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interview Type</Text>
              <TouchableOpacity onPress={() => setShowTypePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={interviewTypes}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: type }) => (
                <TouchableOpacity
                  style={[
                    styles.typeOption,
                    formData.interviewType === type.id && styles.selectedTypeOption,
                  ]}
                  onPress={() => {
                    handleInputChange('interviewType', type.id);
                    handleInputChange('duration', type.duration);
                    handleInputChange('preparationTime', type.preparationTime);
                    setShowTypePicker(false);
                  }}
                >
                  <View style={styles.typeHeader}>
                    <View style={[styles.typeColorIndicator, { backgroundColor: type.color }]} />
                    <View style={styles.typeInfo}>
                      <Text style={[
                        styles.typeName,
                        formData.interviewType === type.id && styles.selectedTypeName,
                      ]}>
                        {type.name}
                      </Text>
                      <Text style={styles.typeDescription}>{type.description}</Text>
                      <View style={styles.typeMeta}>
                        <Text style={styles.typeDuration}>{type.duration} minutes</Text>
                        <Text style={styles.typePrep}>
                          {type.preparationTime} min prep
                        </Text>
                        {type.importance && (
                          <View style={[
                            styles.importanceBadge,
                            { backgroundColor: getImportanceColor(type.importance) }
                          ]}>
                            <Text style={styles.importanceBadgeText}>
                              {String(type.importance ?? '').toUpperCase()}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                    {formData.interviewType === type.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                    )}
                  </View>
                  
                  {type.stages && (
                    <View style={styles.stagesContainer}>
                      <Text style={styles.stagesTitle}>Interview Stages:</Text>
                      <View style={styles.stagesList}>
                        {type.stages.map((stage, index) => (
                          <View key={index} style={styles.stageItem}>
                            <Text style={styles.stageText}>{stage}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render conflict resolution modal
  const renderConflictModal = () => {
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high');
    
    return (
      <Modal visible={showConflictModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Scheduling Conflicts</Text>
              <TouchableOpacity onPress={() => setShowConflictModal(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.conflictContent}>
              <View style={styles.conflictWarning}>
                <MaterialCommunityIcons name="alert-circle" size={48} color="#EF4444" />
                <Text style={styles.conflictTitle}>Cannot Schedule Interview</Text>
                <Text style={styles.conflictSubtitle}>
                  The following conflicts prevent scheduling:
                </Text>
              </View>
              
              <View style={styles.conflictsList}>
                {highSeverityConflicts.map((conflict, index) => (
                  <View key={index} style={styles.conflictItem}>
                    <MaterialCommunityIcons name="close-circle" size={20} color="#EF4444" />
                    <Text style={styles.conflictMessage}>{conflict.message}</Text>
                  </View>
                ))}
              </View>
              
              <View style={styles.conflictActions}>
                <TouchableOpacity
                  style={styles.conflictActionButton}
                  onPress={() => {
                    setShowConflictModal(false);
                    setShowDatePicker(true);
                  }}
                >
                  <MaterialCommunityIcons name="calendar" size={20} color="#4A90E2" />
                  <Text style={styles.conflictActionText}>Choose Different Date</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.conflictActionButton}
                  onPress={() => {
                    setShowConflictModal(false);
                    setShowInterviewerPicker(true);
                  }}
                >
                  <MaterialCommunityIcons name="account-group" size={20} color="#4A90E2" />
                  <Text style={styles.conflictActionText}>Change Interviewers</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // Helper functions for colors
  const getLocationTypeColor = (type) => {
    switch (type) {
      case 'Physical': return '#10B981';
      case 'Virtual': return '#4A90E2';
      case 'Phone': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  const getPrestigeColor = (prestige) => {
    switch (prestige) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getSeniorityColor = (level) => {
    switch (level) {
      case 'C-Level': return '#EF4444';
      case 'VP': return '#F59E0B';
      case 'Director': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'critical': return '#EF4444';
      case 'high': return '#F59E0B';
      case 'medium': return '#10B981';
      default: return '#6B7280';
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading interview data...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
            <Ionicons name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Schedule Final Interview</Text>
          <View style={styles.headerSpacer} />
        </View>
      </LinearGradient>

      <KeyboardAvoidingView
        style={styles.content}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Candidate Info */}
          <View style={styles.candidateCard}>
            <View style={styles.candidateHeader}>
              <View style={styles.candidateAvatar}>
                <Text style={styles.candidateInitials}>
                  {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Text>
              </View>
              <View style={styles.candidateInfo}>
                <Text style={styles.candidateName}>{candidate.name}</Text>
                <Text style={styles.candidatePosition}>{candidate.position}</Text>
                <Text style={styles.candidateEmail}>{candidate.email}</Text>
              </View>
              <View style={styles.finalInterviewBadge}>
                <Text style={styles.finalInterviewBadgeText}>FINAL</Text>
              </View>
            </View>
          </View>

          {/* Conflicts Warning */}
          {conflicts.length > 0 && (
            <View style={styles.conflictsCard}>
              <View style={styles.conflictsHeader}>
                <MaterialCommunityIcons name="alert-circle" size={20} color="#F59E0B" />
                <Text style={styles.conflictsTitle}>Scheduling Conflicts</Text>
              </View>
              {conflicts.map((conflict, index) => (
                <View key={index} style={styles.conflictItemCard}>
                  <MaterialCommunityIcons 
                    name={conflict.severity === 'high' ? 'close-circle' : 'alert'} 
                    size={16} 
                    color={conflict.severity === 'high' ? '#EF4444' : '#F59E0B'} 
                  />
                  <Text style={styles.conflictText}>{conflict.message}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Form Fields */}
          <View style={styles.formContainer}>
            {/* Date Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Date *</Text>
              <TouchableOpacity
                style={[styles.input, errors.date && styles.inputError]}
                onPress={() => setShowDatePicker(true)}
              >
                <MaterialCommunityIcons name="calendar" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.date && styles.placeholderText]}>
                  {formData.date 
                    ? new Date(formData.date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Select date'
                  }
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.date && <Text style={styles.errorText}>{errors.date}</Text>}
            </View>

            {/* Time Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Time *</Text>
              <TouchableOpacity
                style={[styles.input, errors.time && styles.inputError]}
                onPress={() => setShowTimePicker(true)}
                disabled={!formData.date || availableTimeSlots.length === 0}
              >
                <MaterialCommunityIcons name="clock" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.time && styles.placeholderText]}>
                  {formData.time || 'Select time'}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.time && <Text style={styles.errorText}>{errors.time}</Text>}
              {formData.date && availableTimeSlots.length === 0 && (
                <Text style={styles.warningText}>
                  No available time slots for selected date and interviewers
                </Text>
              )}
            </View>

            {/* Location Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Location *</Text>
              <TouchableOpacity
                style={[styles.input, errors.location && styles.inputError]}
                onPress={() => setShowLocationPicker(true)}
              >
                <MaterialCommunityIcons name="map-marker" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.location && styles.placeholderText]}>
                  {safeGetLocationName()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.location && <Text style={styles.errorText}>{errors.location}</Text>}
            </View>

            {/* Interviewers Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interviewers *</Text>
              <TouchableOpacity
                style={[styles.input, errors.interviewers && styles.inputError]}
                onPress={() => setShowInterviewerPicker(true)}
              >
                <MaterialCommunityIcons name="account-group" size={20} color="#64748B" />
                <Text style={[styles.inputText, formData.interviewers.length === 0 && styles.placeholderText]}>
                  {safeGetSelectedInterviewerNames()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.interviewers && <Text style={styles.errorText}>{errors.interviewers}</Text>}
            </View>

            {/* Interview Type Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Type *</Text>
              <TouchableOpacity
                style={[styles.input, errors.interviewType && styles.inputError]}
                onPress={() => setShowTypePicker(true)}
              >
                <MaterialCommunityIcons name="clipboard-text" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.interviewType && styles.placeholderText]}>
                  {safeGetInterviewTypeName()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.interviewType && <Text style={styles.errorText}>{errors.interviewType}</Text>}
            </View>

            {/* Duration */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Duration (minutes)</Text>
              <View style={styles.durationContainer}>
                <TouchableOpacity
                  style={styles.durationButton}
                  onPress={() => handleInputChange('duration', Math.max(30, formData.duration - 15))}
                >
                  <MaterialCommunityIcons name="minus" size={20} color="#64748B" />
                </TouchableOpacity>
                <Text style={styles.durationText}>{formData.duration}</Text>
                <TouchableOpacity
                  style={styles.durationButton}
                  onPress={() => handleInputChange('duration', Math.min(180, formData.duration + 15))}
                >
                  <MaterialCommunityIcons name="plus" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Meeting Link (for virtual meetings) */}
            {formData.location && locations.find(l => l.id === formData.location)?.type === 'Virtual' && (
              <View style={styles.formGroup}>
                <Text style={styles.label}>Meeting Link</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Meeting link will be auto-generated"
                  placeholderTextColor="#94A3B8"
                  value={formData.meetingLink}
                  onChangeText={(text) => handleInputChange('meetingLink', text)}
                  keyboardType="url"
                  autoCapitalize="none"
                />
              </View>
            )}

            {/* Notes */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Notes</Text>
              <TextInput
                style={styles.textArea}
                placeholder="Any special notes or requirements for this final interview..."
                placeholderTextColor="#94A3B8"
                value={formData.notes}
                onChangeText={(text) => handleInputChange('notes', text)}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>

            {/* Notification Settings */}
            <View style={styles.formGroup}>
              <Text style={styles.sectionTitle}>Notification Settings</Text>
              
              <View style={styles.switchContainer}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchLabel}>Notify Candidate</Text>
                  <Text style={styles.switchDescription}>
                    Send email notification to the candidate
                  </Text>
                </View>
                <Switch
                  value={formData.notifyCandidate}
                  onValueChange={(value) => handleInputChange('notifyCandidate', value)}
                  trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                  thumbColor={formData.notifyCandidate ? '#ffffff' : '#f4f3f4'}
                />
              </View>

              <View style={styles.switchContainer}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchLabel}>Notify Interviewers</Text>
                  <Text style={styles.switchDescription}>
                    Send email notifications to all interviewers
                  </Text>
                </View>
                <Switch
                  value={formData.notifyInterviewers}
                  onValueChange={(value) => handleInputChange('notifyInterviewers', value)}
                  trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                  thumbColor={formData.notifyInterviewers ? '#ffffff' : '#f4f3f4'}
                />
              </View>

              <View style={styles.switchContainer}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchLabel}>Send Calendar Invites</Text>
                  <Text style={styles.switchDescription}>
                    Send calendar invites to all participants
                  </Text>
                </View>
                <Switch
                  value={formData.sendCalendarInvite}
                  onValueChange={(value) => handleInputChange('sendCalendarInvite', value)}
                  trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                  thumbColor={formData.sendCalendarInvite ? '#ffffff' : '#f4f3f4'}
                />
              </View>

              <View style={styles.switchContainer}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchLabel}>Record Interview</Text>
                  <Text style={styles.switchDescription}>
                    Record the interview for review purposes
                  </Text>
                </View>
                <Switch
                  value={formData.recordInterview}
                  onValueChange={(value) => handleInputChange('recordInterview', value)}
                  trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                  thumbColor={formData.recordInterview ? '#ffffff' : '#f4f3f4'}
                />
              </View>
            </View>
          </View>
        </ScrollView>

        {/* Action Buttons */}
        <View style={styles.actionContainer}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={handleCancel}
            disabled={isSaving}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.scheduleButton, isSaving && styles.scheduleButtonDisabled]}
            onPress={handleSchedule}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size={20} color="#ffffff" />
            ) : (
              <>
                <MaterialCommunityIcons name="calendar-check" size={20} color="#ffffff" />
                <Text style={styles.scheduleButtonText}>Schedule Interview</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>

      {/* Modals */}
      {renderDatePicker()}
      {renderTimePicker()}
      {renderLocationPicker()}
      {renderInterviewerPicker()}
      {renderTypePicker()}
      {renderConflictModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  content: {
    flex: 1,
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -10,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  candidateCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  candidateAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  candidateInitials: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  candidatePosition: {
    fontSize: 14,
    color: '#4A90E2',
    marginBottom: 2,
  },
  candidateEmail: {
    fontSize: 12,
    color: '#64748B',
  },
  finalInterviewBadge: {
    backgroundColor: '#EF4444',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  finalInterviewBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  conflictsCard: {
    backgroundColor: '#fef3c7',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#fbbf24',
  },
  conflictsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  conflictsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#92400e',
    marginLeft: 8,
  },
  conflictItemCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  conflictText: {
    fontSize: 14,
    color: '#92400e',
    marginLeft: 8,
    flex: 1,
  },
  formContainer: {
    gap: 20,
  },
  formGroup: {
    gap: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 8,
  },
  input: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 12,
  },
  inputError: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  inputText: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
  },
  placeholderText: {
    color: '#94a3b8',
  },
  textArea: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    fontSize: 16,
    color: '#0f172a',
    minHeight: 100,
  },
  errorText: {
    fontSize: 14,
    color: '#ef4444',
    marginTop: 4,
  },
  warningText: {
    fontSize: 14,
    color: '#f59e0b',
    marginTop: 4,
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  durationButton: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  durationText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginHorizontal: 20,
    minWidth: 60,
    textAlign: 'center',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginBottom: 8,
  },
  switchInfo: {
    flex: 1,
    marginRight: 16,
  },
  switchLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 4,
  },
  switchDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  actionContainer: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#f8fafc',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  scheduleButton: {
    flex: 2,
    backgroundColor: '#4A90E2',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  scheduleButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  scheduleButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  modalOption: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#0f172a',
  },
  selectedOption: {
    backgroundColor: '#f0f8ff',
  },
  selectedOptionText: {
    color: '#4A90E2',
    fontWeight: '600',
  },
  dateOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  todayBadge: {
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  todayBadgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  timeSlot: {
    flex: 1,
    margin: 4,
    paddingVertical: 12,
    paddingHorizontal: 8,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedTimeSlot: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  timeSlotText: {
    fontSize: 14,
    color: '#0f172a',
    fontWeight: '600',
  },
  selectedTimeSlotText: {
    color: '#ffffff',
  },
  noAvailabilityContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  noAvailabilityTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  noAvailabilityText: {
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: 20,
  },
  locationOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedLocationOption: {
    borderColor: '#4A90E2',
    backgroundColor: '#f0f8ff',
  },
  locationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  selectedLocationName: {
    color: '#4A90E2',
  },
  locationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  locationTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  locationTypeBadgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  locationCapacity: {
    fontSize: 12,
    color: '#64748B',
  },
  prestigeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  prestigeBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  locationDetails: {
    marginTop: 8,
  },
  locationAddress: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  platformText: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  amenitiesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  amenityChip: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  amenityText: {
    fontSize: 12,
    color: '#0891b2',
  },
  moreAmenities: {
    fontSize: 12,
    color: '#64748B',
    fontStyle: 'italic',
  },
  featuresContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  featureChip: {
    backgroundColor: '#dbeafe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  featureText: {
    fontSize: 12,
    color: '#1d4ed8',
  },
  interviewerOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedInterviewerOption: {
    borderColor: '#10b981',
    backgroundColor: '#f0fdf4',
  },
  unavailableInterviewerOption: {
    opacity: 0.6,
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
  },
  interviewerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  interviewerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  interviewerInitials: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  selectedInterviewerName: {
    color: '#10b981',
  },
  interviewerTitle: {
    fontSize: 14,
    color: '#4A90E2',
    marginBottom: 1,
  },
  interviewerDepartment: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  interviewerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  ratingText: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '600',
  },
  seniorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  seniorityBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  selectionIndicator: {
    marginLeft: 8,
  },
  availabilityIndicator: {
    marginTop: 8,
    marginBottom: 4,
  },
  availableIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  availableText: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '600',
  },
  unavailableIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  unavailableText: {
    fontSize: 12,
    color: '#ef4444',
    fontWeight: '600',
  },
  expertiseContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 8,
  },
  expertiseChip: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  expertiseText: {
    fontSize: 12,
    color: '#0891b2',
  },
  moreExpertise: {
    fontSize: 12,
    color: '#64748B',
    fontStyle: 'italic',
  },
  typeOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedTypeOption: {
    borderColor: '#4A90E2',
    backgroundColor: '#f0f8ff',
  },
  typeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  typeColorIndicator: {
    width: 4,
    height: 40,
    borderRadius: 2,
    marginRight: 12,
  },
  typeInfo: {
    flex: 1,
  },
  typeName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  selectedTypeName: {
    color: '#4A90E2',
  },
  typeDescription: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  typeMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  typeDuration: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '600',
  },
  typePrep: {
    fontSize: 12,
    color: '#f59e0b',
    fontWeight: '600',
  },
  importanceBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  importanceBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  stagesContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
  },
  stagesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  stagesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  stageItem: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  stageText: {
    fontSize: 12,
    color: '#0891b2',
  },
  conflictContent: {
    padding: 20,
  },
  conflictWarning: {
    alignItems: 'center',
    marginBottom: 24,
  },
  conflictTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ef4444',
    marginTop: 12,
    marginBottom: 8,
  },
  conflictSubtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  conflictsList: {
    marginBottom: 24,
  },
  conflictItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  conflictMessage: {
    fontSize: 14,
    color: '#dc2626',
    marginLeft: 8,
    flex: 1,
  },
  conflictActions: {
    gap: 12,
  },
  conflictActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f8ff',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
    gap: 8,
  },
  conflictActionText: {
    fontSize: 16,
    color: '#4A90E2',
    fontWeight: '600',
  },
});

export default ScheduleFinalInterviewScreen;
